import PropTypes from "prop-types";
import { memo, useMemo } from "react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import SwiperCore, { Navigation, Pagination, A11y } from "swiper";
import { SwiperSlide, Swiper } from "swiper/react";

import { Link } from "../al_components/ALLink";
import useDeviceDetect from "../../hooks/useDeviceDetect";
import { useLocalizedSentenceDict } from "../../hooks/useSentenceDict";
import useResponsiveWithHydrationFix from "../../hooks/useResponsiveWithHydrationFix";
import { ComponentType } from "../../constants/ComponentType";

import IconArrowCircleMd from "../icons/svgs/arrow-circle-md.svg";

import "swiper/swiper.min.css";
import "swiper/swiper-bundle.min.css";
import * as Styles from "./scrollableImages/scrollableImagesStyles.module.scss";

SwiperCore.use([Navigation, Pagination, A11y]);

function ScrollableImages({ content, componentType }) {
  const { title, images } = content;
  const dict = useLocalizedSentenceDict();
  const { isMobile } = useDeviceDetect();

  const additionalClassName = componentType ? `${Styles[componentType.toLowerCase()]}` : "";

  const imagesToRender = useMemo(
    () =>
      images.reduce((imageCards, { link, altText, desktopImage, mobileImage }) => {
        let imgContent = null;
        let linkContent = null;
        if (isMobile) {
          if (!mobileImage.url.includes("empty.png")) {
            imgContent = (
              <GatsbyImage
                key={mobileImage.id}
                className={Styles.scrollableImages__mobile__image}
                alt={altText}
                image={getImage(mobileImage.gatsbyImageData)}
              />
            );
            if (link && link?.title !== "empty") {
              linkContent = (
                <Link key={`scrollable_${link.link.link}`} to={link.link.link}>
                  {imgContent}
                </Link>
              );
            }

            imageCards.push(linkContent || imgContent);
          }
        } else if (!desktopImage.url.includes("empty.png")) {
          imgContent = (
            <GatsbyImage
              className={Styles.scrollableImages__desktop__image}
              alt={altText}
              image={getImage(desktopImage.gatsbyImageData)}
            />
          );
          if (link && link?.title !== "empty") {
            linkContent = (
              <Link key={`scrollable_${link.link.link}`} to={link.link.link}>
                {imgContent}
              </Link>
            );
          }

          imageCards.push(
            <SwiperSlide key={desktopImage.id}>{linkContent || imgContent}</SwiperSlide>
          );
        }
        return imageCards;
      }, []),
    [images, isMobile]
  );

  const MobileLayout = <div className={Styles.scrollableImages__mobile}>{imagesToRender}</div>;

  const DesktopButtons = useResponsiveWithHydrationFix(
    null,
    <div className={Styles.buttons}>
      <button
        className={`scrollable-prev-button ${Styles.scrollablePrevButton}`}
        type="button"
        aria-label={dict.get("previous")}
      >
        <IconArrowCircleMd width="35" stroke="#2D2927" />
      </button>
      <button
        className={`scrollable-next-button ${Styles.scrollableNextButton}`}
        type="button"
        aria-label={dict.get("next")}
      >
        <IconArrowCircleMd width="35" stroke="#2D2927" />
      </button>
    </div>
  );

  const DesktopLayout = (
    <Swiper
      navigation={{
        nextEl: ".scrollable-next-button",
        prevEl: ".scrollable-prev-button",
      }}
      slidesPerView={4}
      slidesPerGroup={4}
    >
      <div>{imagesToRender}</div>
    </Swiper>
  );

  const imageLayout = useResponsiveWithHydrationFix(MobileLayout, DesktopLayout);

  return (
    <div className={`${Styles.scrollableImages__container} id-${content.contentful_id}`}>
      <div className={`${Styles.scrollableImages__header} ${additionalClassName}`}>
        <h2 className="h2 h2--bold h2--uc mt-0 mb-0">{dict.get(title)}</h2>
        {DesktopButtons}
      </div>
      {imageLayout}
    </div>
  );
}

export default memo(ScrollableImages);

ScrollableImages.propTypes = {
  content: PropTypes.shape({
    contentful_id: PropTypes.string,
    title: PropTypes.string,
    images: PropTypes.arrayOf(
      PropTypes.shape({
        altText: PropTypes.string,
        desktopImage: PropTypes.shape({
          url: PropTypes.string,
          gatsbyImageData: PropTypes.object.isRequired,
        }).isRequired,
        mobileImage: PropTypes.shape({
          url: PropTypes.string,
          gatsbyImageData: PropTypes.object.isRequired,
        }).isRequired,
      })
    ).isRequired,
  }).isRequired,
  componentType: PropTypes.oneOf([ComponentType.PDP, ComponentType.HP]),
};
